import React, { useState, useEffect } from 'react';
import {
  Box,
  Flex,
  Text,
  Icon,
  Button,
  SimpleGrid,
  Spinner,
  VStack,
  useToast
} from '@chakra-ui/react';
import { FiUsers, FiArrowRight, FiRefreshCw } from 'react-icons/fi';
import { useHistory } from 'react-router-dom';

// Components
import Card from 'components/Card/Card.js';

// Services
import { getRecentUsers } from 'services/unifiedCustomersSearch';

// Utils
import { AVATAR_GRADIENTS, getInitials } from 'utils/avatarStyles';

const StudentCard = ({ user, onClick, gradient }) => {
  const fullName = user.name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'Sin nombre';
  const group = user.meta_data?.find(m => m.key === 'group_name')?.value || user.group;
  const sub = [user.username, group].filter(Boolean).join(' · ');

  return (
    <Flex
      align="center"
      gap="11px"
      bg="cream.bg"
      border="1px solid"
      borderColor="cream.line"
      borderRadius="13px"
      p="12px 14px"
      cursor="pointer"
      onClick={() => onClick(user)}
      _hover={{ borderColor: 'coral.500', bg: 'coral.tint' }}
      transition="all 0.15s"
      minW={0}
    >
      <Flex
        w="40px"
        h="40px"
        borderRadius="50%"
        bgGradient={gradient}
        align="center"
        justify="center"
        fontWeight="700"
        color="white"
        fontSize="13px"
        flexShrink={0}
      >
        {getInitials(fullName)}
      </Flex>
      <Box minW={0}>
        <Text fontWeight="700" fontSize="13.5px" letterSpacing="-0.01em" lineHeight="1.2" noOfLines={1}>
          {fullName}
        </Text>
        <Text fontSize="11.5px" color="ink.400" fontWeight="500" mt="2px" noOfLines={1}>
          {sub || user.email || 'Sin información'}
        </Text>
      </Box>
    </Flex>
  );
};

const RecentUsers = () => {
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const history = useHistory();
  const toast = useToast();

  const loadRecentUsers = async (showToast = false) => {
    try {
      setIsLoading(true);
      const results = await getRecentUsers(6);
      setUsers(Array.isArray(results) ? results : []);

      if (showToast) {
        toast({
          title: "Alumnos actualizados",
          status: "success",
          duration: 2000,
          isClosable: true,
          position: "top-right"
        });
      }
    } catch (error) {
      console.error('❌ RECENT USERS: Error cargando usuarios:', error);
      toast({
        title: "Error al cargar alumnos",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true
      });
      setUsers([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadRecentUsers(false);
  }, []);

  const handleUserClick = (user) => {
    if (user.id) {
      history.push(`/admin/usuario/${user.id}`);
    } else {
      toast({
        title: "Error de navegación",
        description: "El usuario no tiene ID válido",
        status: "error",
        duration: 3000,
        isClosable: true
      });
    }
  };

  return (
    <Card p={0} borderRadius="22px" overflow="hidden">
      <Flex align="center" gap="10px" p="18px 22px 6px">
        <Text fontSize="16px" fontWeight="800" letterSpacing="-0.01em" whiteSpace="nowrap">
          Alumnos recientes
        </Text>
        <Box fontSize="12px" fontWeight="700" color="ink.400" bg="cream.bg2" px="9px" py="3px" borderRadius="999px" whiteSpace="nowrap">
          {users.length} {users.length === 1 ? 'nuevo' : 'nuevos'}
        </Box>
        <Flex ml="auto" align="center" gap="12px">
          <Button
            size="xs"
            variant="ghost"
            leftIcon={<Icon as={FiRefreshCw} />}
            onClick={() => loadRecentUsers(true)}
            isLoading={isLoading}
          >
            Actualizar
          </Button>
          <Box
            as="span"
            fontSize="13px"
            fontWeight="700"
            color="coral.700"
            whiteSpace="nowrap"
            cursor="pointer"
            onClick={() => history.push('/admin/usuarios')}
            display="flex"
            alignItems="center"
            gap="4px"
          >
            Ver todos <Icon as={FiArrowRight} />
          </Box>
        </Flex>
      </Flex>

      <Box p="14px 18px 18px">
        {isLoading ? (
          <Flex justify="center" py="24px">
            <Spinner color="coral.500" />
          </Flex>
        ) : users.length === 0 ? (
          <VStack py="24px" spacing={2} color="ink.400">
            <Icon as={FiUsers} boxSize="24px" />
            <Text fontSize="sm">No hay alumnos registrados</Text>
          </VStack>
        ) : (
          <SimpleGrid columns={{ base: 1, sm: 2 }} spacing="10px">
            {users.map((user, index) => (
              <StudentCard
                key={`${user.id}-${index}`}
                user={user}
                onClick={handleUserClick}
                gradient={AVATAR_GRADIENTS[index % AVATAR_GRADIENTS.length]}
              />
            ))}
          </SimpleGrid>
        )}
      </Box>
    </Card>
  );
};

export default RecentUsers;
